import {
  AttendeeType,
  ControlType,
  CustomFieldDefinition,
  CustomFieldUsage,
  CustomFieldValueDefinition,
  CustomFormRecordStatus,
  FieldDataType,
  FieldStatus,
  MeetingAttendeeStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  VEEVA_VAULT_TABLES,
  VeevaVaultFormSetting,
  VeevaVaultObjectField,
  VeevaVaultObjectFieldType,
  VeevaVaultObjectLayoutSection,
  VeevaVaultPicklist,
  VeevaVaultUpsertObjectPayload,
  VeevaVaultUpsertRecord,
} from 'src/classes/CRM/CRMVeevaVaultTypes';
import type { FormValuesType } from 'src/components/CustomFields/CustomFieldValueUtilities';
import {
  UpsertObjectsResult,
  VEEVA_VAULT_FIELDS_TO_IGNORE_FOR_SUBMISSION,
  VEEVA_VAULT_FIELDS_TO_IGNORE_ON_UPDATE,
} from '../../Syncers/VeevaVault/VeevaVaultSyncerUtils';
import { isArrayOfObjectsWithIds } from 'src/types/typeguards';
import logger from 'src/utils/logger';
import { CRMSubmitMeetingPayload } from '../../CRMIndexedDBTypes';
import { AttendeesAccounts } from '../VeevaFormTranslator';
import { VEEVA_VAULT_TABLE_VALUES_MAP } from './VeevaVaultAdditionalValuesHandler';

export const SECTIONS_TO_SKIP: { [tableName: string]: string[] } = {
  [VEEVA_VAULT_TABLES.CALL]: [
    'buttons__c',
    'attendee_specific_fields__c', // CURRENTLY NOT SUPPORTED.
  ],
}

//* FORM METADATA TRANSLATION UTILITIES *//
export function getFormattedDependentPicklistValueDefinitions(
  picklistData: VeevaVaultPicklist): CustomFieldValueDefinition[] {
  const definitions = new Map<string, CustomFieldValueDefinition>();
  Object.entries(picklistData.picklistDependencies || {}).forEach(([controllerValue, dependentValues]) => {
    dependentValues.forEach((picklistValue) => {
      const existing = definitions.get(picklistValue);
      if (existing) {
        definitions.set(picklistValue, {
          ...existing,
          dependentValueIds: [...new Set([...existing.dependentValueIds, controllerValue])],
        });
      } else {
        definitions.set(picklistValue, {
          ...getPartialCustomFieldValueDefinitions(),
          id: picklistValue,
          value: picklistValue,
          label: picklistData.picklistValues.find(({ name }) => name === picklistValue)?.label,
          dependentValueIds: [controllerValue],
        });
      }
    });
  });
  return [...definitions.values()];
}

export function resolveProductMapPicklist(
  picklist: VeevaVaultPicklist,
  setting: VeevaVaultFormSetting,
): VeevaVaultPicklist {
  if (setting.apiName !== VEEVA_VAULT_TABLES.CALL_DISCUSSION ||
    picklist.controllingPicklistName !== 'product_map__v' || !picklist.picklistDependencies) {
    return picklist;
  }

  const dependencies: Record<string, string[]> = {};
  const productMapValues = setting.picklists.product_map__v?.picklistValues || [];
  Object.entries(picklist.picklistDependencies).forEach(([value, topics]) => {
    const productName = productMapValues.find(option => option.name === value)?.label || value;
    (setting.lookups.product__v || [])
      .filter(product => product.id === value || product.name__v === productName)
      .forEach(product => {
        dependencies[product.id] = [...new Set([...(dependencies[product.id] || []), ...topics])];
      });
  });

  return {
    ...picklist,
    controllingPicklistName: 'product__v',
    picklistDependencies: dependencies,
  };
}

export function getPartialCustomField(field: VeevaVaultObjectField):
  Omit<CustomFieldDefinition, 'fieldType' | 'controlType'> {
  return {
    id: field.name,
    fieldLabel: field.label || field.name,
    createdAt: new Date().toISOString(),
    fieldName: field.name,
    fieldValueDefinitions: getDefaultFieldValueDefinitions(field),
    status: FieldStatus.ENABLED,
    usage: [CustomFieldUsage.MEETING],
  };
}

function getDefaultFieldValueDefinitions(field: VeevaVaultObjectField): CustomFieldValueDefinition[] {
  const fieldValueDefinitions: CustomFieldValueDefinition[] = [];

  if (field.default_value) {
    fieldValueDefinitions.push({
      ...getPartialCustomFieldValueDefinitions(),
      id: field.default_value,
      isDefault: true,
      label: field.default_value.replace('{', '').replace('}', ''),
      value: field.default_value,
    });
  }

  return fieldValueDefinitions;
}

export function getPartialCustomFieldValueDefinitions():
  Omit<CustomFieldValueDefinition, 'id' | 'value'> {
  return {
    createdAt: new Date().toISOString(),
    dependentValueIds: [],
    label: '',
  };
}

export function getSectionLabel(section: VeevaVaultObjectLayoutSection): CustomFieldDefinition {
  return {
    id: section.name,
    controlType: ControlType.LABEL,
    fieldType: FieldDataType.STRING,
    fieldLabel: section.title,
    fieldName: section.name,
    createdAt: new Date().toISOString(),
    usage: [CustomFieldUsage.MEETING],
    status: FieldStatus.ENABLED,
    fieldValueDefinitions: [],
  };
}

export function shouldSectionBeIgnored(
  section: VeevaVaultObjectLayoutSection,
  veevaVaultFormSetting: VeevaVaultFormSetting,
): boolean {
  return SECTIONS_TO_SKIP[veevaVaultFormSetting.apiName]?.includes(section.name) ?? false;
}

//* END FORM METADATA TRANSLATION UTILITIES *//

//* SUBMISSION TRANSLATION UTILITIES *//
export type VeevaVaultBuildUpsertObjectOptions = {
  attendeesMapped?: AttendeesAccounts,
  crmValues: FormValuesType,
  crmSubmitPayload?: CRMSubmitMeetingPayload,
  objectFormSetting: VeevaVaultFormSetting,
  objectType?: string,
  parentObject?: VeevaVaultUpsertRecord,
  parentObjectExternalId?: string,
  referenceId: string,
  isUpdate?: boolean,
  isSubmit?: boolean,
}

// TURNS "medical_discussion__v-OBJECT_TYPE-base__v-FIELD_NAME-product__v" INTO "medical_discussion__v"
export function getObjectAPINameFromFieldKey(formKey: string): string {
  return formKey.split('-OBJECT_TYPE-')[0];
}

// TURNS "medical_discussion__v-OBJECT_TYPE-base__v-FIELD_NAME-product__v" INTO "product__v"
export function getFieldApiName(formKey: string): string {
  return formKey.split('-FIELD_NAME-')[1] || formKey;
}

// TURNS "medical_discussion__v-OBJECT_TYPE-base__v" INTO "base__v"
function getObjectTypeFromObjectFieldKey(formKey: string): string | undefined {
  return formKey.split('-OBJECT_TYPE-')[1];
}

function getBaseUpsertObjectRecordPayload(
  crmValues: FormValuesType,
  referenceId: string,
  formSetting: VeevaVaultFormSetting,
  isUpdate?: boolean,
): VeevaVaultUpsertObjectPayload {
  const record: VeevaVaultUpsertObjectPayload = {
    objectAPIName: formSetting.apiName,
    object: {},
    referenceId,
  };

  Object.entries(crmValues).forEach(([formKey, value]) => {
    const key = getFieldApiName(formKey);
    const fieldDefinition = formSetting.objectInfo.fields
      .find((field) => field.name === key);
    const isFieldToIgnore = VEEVA_VAULT_FIELDS_TO_IGNORE_FOR_SUBMISSION[formSetting.apiName]?.includes(key) ||
        (!!isUpdate && VEEVA_VAULT_FIELDS_TO_IGNORE_ON_UPDATE[formSetting.apiName]?.includes(key));
    if (value !== undefined && value !== null &&
      (fieldDefinition?.editable || fieldDefinition?.type === VeevaVaultObjectFieldType.ID) &&
      !isArrayOfObjectsWithIds(value) &&
      !isFieldToIgnore) {
      record.object[key] = processUpsertFieldValue(fieldDefinition, value);
    }
  });

  return record;
}

// RECEIVES AND ARRAY OF ATTENDEES AND CREATES A "MAIN OBJECT" PAYLOAD PER ATTENDEE
export function handleAttendeeObjectsPayloadsCreation(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainFormSetting: VeevaVaultFormSetting,
  mainObjectVeevaVaultId: string,
  mainObject: VeevaVaultUpsertRecord,
  attendees: AttendeesAccounts): VeevaVaultUpsertObjectPayload[] {
  logger.veevaTranslator.info('Handling attendees objects payload creation...');
  const payloads: VeevaVaultUpsertObjectPayload[] = [];
  const existingAttendees = crmSubmitPayload.meetingORM.model.attendees;

  // PAYLOADS FOR ACCOUNT ATTENDEES
  attendees.additionalAttendees.forEach((attendee) => {
    if (attendee.account) {
      const attendeeCRMRecordId = existingAttendees.find((existingAttendee) =>
        existingAttendee.id === attendee.attendeeForm.id)?.crmRecord?.crmCallId;

      const attendeeObject: VeevaVaultUpsertObjectPayload = {
        referenceId: attendee.attendeeForm.id,
        objectAPIName: mainFormSetting.apiName,
        object: {
          ...mainObject,
          id: attendeeCRMRecordId || null,
          account__v: attendee.account.id,
          attendees__v: 0,
          attendee_type__v: attendee.account.ispersonaccount__v ? 'person_account__v' : 'group_account__v',
          credentials__v: attendee.account.credentials__v ? attendee.account.credentials__v.toString() : null,
          parent_call__v: mainObjectVeevaVaultId,
        },
      };

      logger.veevaTranslator.info(`Adding account attendee ${attendee.account.id}...`);
      payloads.push(attendeeObject);
    }
  });

  // PAYLOADS FOR USER ATTENDEES
  crmSubmitPayload.formValuesAttendees.forEach((attendee) => {
    const shouldCreateCallForUserAttendee = attendee.attendeeType === AttendeeType.CRM_USER &&
      attendee.status === MeetingAttendeeStatus.ACTIVE && attendee.crmUserId;

    if (shouldCreateCallForUserAttendee) {
      const userCRMRecordId = existingAttendees.find((existingAttendee) =>
        existingAttendee.id === attendee.id)?.crmRecord?.crmCallId;

      const userCallObject: VeevaVaultUpsertObjectPayload = {
        referenceId: attendee.id,
        objectAPIName: mainFormSetting.apiName,
        object: {
          ...mainObject,
          id: userCRMRecordId || null,
          account__v: null,
          attendee_type__v: 'user__v',
          attendees__v: 0,
          credentials__v: null,
          parent_call__v: mainObjectVeevaVaultId,
          user__v: attendee.crmUserId,
        },
      };

      logger.veevaTranslator.info(`Adding user attendee ${attendee.crmUserId}...`);
      payloads.push(userCallObject);
    }
  });

  payloads.forEach(({ object }) => {
    if (!object.id && mainFormSetting.objectType) {
      object['object_type__v.api_name__v'] = mainFormSetting.objectType;
    }
  });

  logger.veevaTranslator.info('Attendees payloads created: ', payloads);
  return payloads;
}

// CREATES THE CHILD OBJECTS OF THE ATTENDEES
export async function handleChildObjectsPayloadsCreation(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  upsertedParentObjects: UpsertObjectsResult,
  attendees: AttendeesAccounts,
): Promise<VeevaVaultUpsertObjectPayload[]> {
  logger.veevaTranslator.info('Handling child objects payloads creation...');
  const payloads: VeevaVaultUpsertObjectPayload[] = [];

  for (const attendee of attendees.additionalAttendees) {
    const attendeePayloads = await getAttendeeChildObjectsPayloads(
      attendee.attendeeForm.id,
      attendee.attendeeForm.crmValues || {},
      crmSubmitPayload, upsertedParentObjects);
    payloads.push(...attendeePayloads);
  }

  for (const attendee of crmSubmitPayload.formValuesAttendees) {
    if (attendee.attendeeType === AttendeeType.CRM_USER && attendee.status === MeetingAttendeeStatus.ACTIVE) {
      const userAttendeePayloads = await getAttendeeChildObjectsPayloads(
        attendee.id,
        attendee.crmValues || {},
        crmSubmitPayload, upsertedParentObjects);
      payloads.push(...userAttendeePayloads);
    }
  }

  const mainAttendeePayloads = await getAttendeeChildObjectsPayloads(
    'main-record',
    crmSubmitPayload.mainCrmValues,
    crmSubmitPayload,
    upsertedParentObjects,
  );

  payloads.push(...mainAttendeePayloads);

  logger.veevaTranslator.info('Child records payloads: ', payloads);
  return payloads;
}

// MAIN FUNCTION TO CREATE THE PAYLOADS BASED ON THE FORM VALUES
export async function buildUpsertObjectRecord(
  options: VeevaVaultBuildUpsertObjectOptions): Promise<VeevaVaultUpsertObjectPayload> {
  logger.veevaTranslator.info(`Handling creation of ${options.objectFormSetting.apiName} object... `);
  const { crmValues, objectFormSetting, referenceId, objectType } = options;

  const record: VeevaVaultUpsertObjectPayload =
      getBaseUpsertObjectRecordPayload(crmValues, referenceId, objectFormSetting, options.isUpdate);

  // INCLUDES VEEVA SPECIFIC FIELDS ACCORDING TO THE OBJECT
  const additionalValuesHandler = VEEVA_VAULT_TABLE_VALUES_MAP[objectFormSetting.apiName];

  if (additionalValuesHandler) {
    const additionalValues = await additionalValuesHandler(options);
    record.object = {
      ...additionalValues,
      ...record.object,
    };
  }

  if (options.parentObjectExternalId && objectFormSetting.parentTableRelationshipName) {
    record.object[objectFormSetting.parentTableRelationshipName] = options.parentObjectExternalId;
  }

  if (objectType && !options.isUpdate) {
    record.object['object_type__v.api_name__v'] = objectType;
  }

  return record;
}

export async function handleCustomFormObjectsPayloadsCreation(
  crmSubmitPayload: CRMSubmitMeetingPayload,
  mainObjectVeevaVaultId: string,
) {
  logger.veevaTranslator.info('Handling custom form records payloads creation...');
  const payloads: VeevaVaultUpsertObjectPayload[] = [];

  for (const recordCustomForm of crmSubmitPayload.crmCustomFormRecords) {
    if (recordCustomForm.model.status !== CustomFormRecordStatus.ACTIVE) {
      logger.veevaVaultSyncer.warn(`Skipping non-active record: ${recordCustomForm.model.id}`);
      continue;
    }

    const recordAPIName = recordCustomForm.model.crmFields?.apiName;
    const formSetting = crmSubmitPayload.veevaVaultFormSettings.find((formSetting) =>
      formSetting.apiName === recordAPIName);

    if (!formSetting || !recordAPIName) {
      logger.veevaVaultSyncer.error(`Form setting not found for API Name: ${recordAPIName}`);
      throw new Error(`Form setting not found for API Name: ${recordAPIName}`);
    }

    const externalId = recordCustomForm.model.crmFields?.externalId;
    const isUpdate = !!externalId;

    // For updates, include the external ID in crmValues as 'id'
    const crmValuesForUpdate = isUpdate
      ? { ...recordCustomForm.meta.formValues, id: externalId }
      : recordCustomForm.meta.formValues;

    const payload = await buildUpsertObjectRecord({
      objectFormSetting: formSetting,
      crmValues: crmValuesForUpdate,
      referenceId: recordCustomForm.model.id,
      objectType: formSetting.objectType,
      parentObjectExternalId: mainObjectVeevaVaultId,
      isSubmit: crmSubmitPayload.submitType === 3, // LOCKED TO CRM SUBMIT TYPE
      isUpdate,
    });

    payloads.push(payload);
  }

  return payloads;
}

async function getAttendeeChildObjectsPayloads(
  referenceId: string,
  crmValues: FormValuesType,
  crmSubmitPayload: CRMSubmitMeetingPayload,
  upsertedParentObjects: UpsertObjectsResult,
): Promise<VeevaVaultUpsertObjectPayload[]> {
  logger.veevaTranslator.info('Handling child objects of: ', referenceId);

  const payloads: VeevaVaultUpsertObjectPayload[] = [];
  const attendeeCallId = upsertedParentObjects.response[referenceId]?.externalId;
  const attendeeCallObject = upsertedParentObjects.objects.find((object) => object.referenceId === referenceId);

  if (!attendeeCallId) {
    logger.veevaTranslator.error(`External id not found for ${referenceId}. Skipping child objects creation...`);
    return [];
  }

  for (const [formKey, value] of Object.entries(crmValues)) {
    if (Array.isArray(value) && isArrayOfObjectsWithIds(value)) {
      const apiName = getObjectAPINameFromFieldKey(formKey);
      const objectType = getObjectTypeFromObjectFieldKey(formKey);
      const objectFormSetting =
        crmSubmitPayload.veevaVaultFormSettings.find((formSetting) => formSetting.apiName === apiName &&
          (!objectType || formSetting.objectType === objectType || formSetting.layout.object_type === objectType));

      if (objectFormSetting) {
        for (const object of value) {
          const { id, status, syncStatus, externalId, ...rest } = object;
          const objectPayload = await buildUpsertObjectRecord({
            crmValues: { ...rest, id: externalId || '' },
            crmSubmitPayload,
            referenceId: id,
            parentObjectExternalId: attendeeCallId,
            parentObject: attendeeCallObject?.object,
            objectType,
            isUpdate: !!externalId,
            objectFormSetting,
          });
          payloads.push(objectPayload);
        }
      } else {
        logger.veevaVaultSyncer.error(`Form setting not found for API Name: ${apiName}`);
        throw new Error(`Form setting not found for API Name: ${apiName}`);
      }
    }
  }

  return payloads;
}

function processUpsertFieldValue(
  fieldDefinition: VeevaVaultObjectField,
  val: string | string[],
): string | number | string[] | boolean | null {
  if (fieldDefinition.multi_value) {
    return val;
  }

  let value = val;
  if (Array.isArray(value)) {
    value = value.join(', ');
  }

  switch (fieldDefinition.type) {
    case VeevaVaultObjectFieldType.NUMBER:
      return Number(value);
    case VeevaVaultObjectFieldType.BOOLEAN:
      if (Array.isArray(value)) {
        return value[0] === 'true';
      } else {
        return value === 'true';
      }
    case VeevaVaultObjectFieldType.DATE:
      return value.toString().split('T')[0];
  }

  return value ?? null;
}

export function getFieldDefinitionFromFormSettings(
  formSettings: VeevaVaultFormSetting[],
  tableName: string,
  fieldName: string): VeevaVaultObjectField | undefined {
  const formSetting = formSettings.find(({ apiName }) => apiName === tableName);
  return formSetting?.objectInfo?.fields.find(({ name }) => name === fieldName);
}
